import { defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils';
import { useEffect, useRef, useState } from 'react';
import { USER_INTERACTION } from '../../../../packages/constants/entityTypes';
import { SHOW_MORE_SUBSCRIBE_TYPE, SHOW_PREVIOUS_SUBSCRIBE_TYPE } from '../../../../packages/constants/subscribeTypes';
import { IMultisportCompetitionEvents, ISubscriberInfo } from '../../../../packages/types';

const EXTRA_SCROLL_SIZE = 100;

interface IUseScrollManagerArgs {
  isLoadingEvents: boolean;
  isLoadingCompetitions: boolean;
  competitionEvents: IMultisportCompetitionEvents | undefined;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
  competitions?: string[] | null;
}

export default function useScrollManager({
  isLoadingEvents,
  isLoadingCompetitions,
  competitionEvents,
  handleClick,
  competitions,
}: IUseScrollManagerArgs) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasCompetitionsChanged, setHasCompetitionsChanged] = useState(false);

  const handleShowMoreEventsClick = () => defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, SHOW_MORE_SUBSCRIBE_TYPE);

  const handleShowPreviousEventsClick = () =>
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, SHOW_PREVIOUS_SUBSCRIBE_TYPE);

  const handleRightClick = () => {
    const element = containerRef.current;
    if (element) {
      const width = element.offsetWidth;
      const newScrollPosition = element.scrollLeft + width;
      element.scrollLeft = element.scrollWidth - newScrollPosition < width ? element.scrollWidth : newScrollPosition;
      handleShowMoreEventsClick();
    }
  };

  const handleLeftClick = () => {
    const element = containerRef.current;
    if (element) {
      const width = element.offsetWidth;
      const newScrollPosition = element.scrollLeft - width;
      element.scrollLeft = element.scrollLeft - width < EXTRA_SCROLL_SIZE ? 0 : newScrollPosition;
      handleShowPreviousEventsClick();
    }
  };

  useEffect(() => {
    const element = containerRef.current;
    if (element) {
      const checkScroll = () => {
        const maxScroll = element.scrollWidth - element.clientWidth;
        setCanScrollLeft(!(element.scrollLeft < EXTRA_SCROLL_SIZE));
        setCanScrollRight(maxScroll > 0 && maxScroll - element.scrollLeft > EXTRA_SCROLL_SIZE);
      };

      element.addEventListener('scroll', checkScroll);

      return () => {
        element.removeEventListener('scroll', checkScroll);
      };
    }
  }, [isLoadingEvents, isLoadingCompetitions]);

  useEffect(() => {
    const element = containerRef.current;

    if (element) {
      element.scrollTo({ left: 0 });
      setCanScrollLeft(false);
      const maxScroll = element.scrollWidth - element.clientWidth;
      setCanScrollRight(maxScroll > 0);
    }
  }, [competitionEvents, isLoadingEvents, isLoadingCompetitions]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (containerRef.current) {
        sessionStorage.setItem('competitions', JSON.stringify(competitions) ?? '');
        sessionStorage.setItem('scrollPosition', containerRef.current.scrollLeft.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const saveCompetitions = sessionStorage.getItem('competitions');

    if (!competitions) {
      setHasCompetitionsChanged(true);
    }

    if (competitions && competitions.length > 0) {
      const isChanged = saveCompetitions !== JSON.stringify(competitions);
      setHasCompetitionsChanged(isChanged);
    }
  }, [isLoadingEvents, isLoadingCompetitions, competitions]);

  useEffect(() => {
    const element = containerRef.current;
    const savedScrollPosition = Number(sessionStorage.getItem('scrollPosition'));

    if (!isLoadingEvents && !isLoadingCompetitions && element && !canScrollLeft && !hasCompetitionsChanged) {
      element.scrollTo({ left: savedScrollPosition, behavior: 'auto' });

      const handleScroll = () => {
        sessionStorage.removeItem('scrollPosition');
        sessionStorage.removeItem('competitions');
        element.removeEventListener('scroll', handleScroll);
      };

      element.addEventListener('scroll', handleScroll);

      return () => {
        element.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isLoadingEvents, isLoadingCompetitions, competitions]);

  return { containerRef, canScrollLeft, canScrollRight, handleLeftClick, handleRightClick };
}
